import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  LinearProgress,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { pageColors } from '../pages/HomePage';
import apiFetch from '../api/apiFetch';

export default function ArticleStatsPage({ connected, setConnected, setError }) {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [stats, setStats] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('articleNumber');
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!connected) {
      setError('Not connected to Fortnox. Please connect first.');
      setConnected(false);
      return;
    }
    if (!startDate) {
      setError('Please pick a start date.');
      return;
    }
    if (!endDate) {
      setError('Please pick an end date.');
      return;
    }
    const s = startDate.format('YYYY-MM-DD');
    const e = endDate.format('YYYY-MM-DD');

    setLoading(true);

    try {
      const response = await apiFetch(`/api/article-stats?startDate=${s}&endDate=${e}`);
      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 401) {
          setConnected(false);
          setError('Lost connection to Fortnox: Unauthorized. Please reconnect.');
        } else {
          setConnected(false);
          setError(`Failed to fetch invoices: ${errorText || response.status}. Please check your connection and try again.`);
        }
        return;
      }
      const data = await response.json();
      setStats(data);
      setConnected(true);
    } catch (err) {
      setConnected(false);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const comparator = (a, b) => {
    let valA, valB;
    switch (orderBy) {
      case 'articleNumber':
        valA = a.articleNumber ?? '';
        valB = b.articleNumber ?? '';
        break;
      case 'articleDescription':
        valA = a.articleDescription ?? '';
        valB = b.articleDescription ?? '';
        break;
      case 'totalQuantity':
        valA = a.totalQuantity ?? 0;
        valB = b.totalQuantity ?? 0;
        break;
      case 'totalSales':
        valA = a.totalSales ?? 0;
        valB = b.totalSales ?? 0;
        break;
      default:
        valA = '';
        valB = '';
        break;
    }
    if (valA < valB) return order === 'asc' ? -1 : 1;
    if (valA > valB) return order === 'asc' ? 1 : -1;
    return 0;
  };

  const sortedStats = [...stats].sort(comparator);

  // Create a theme using the Article Stats color
  const articleStatsTheme = createTheme({
    palette: {
      primary: {
        main: pageColors.articleStats,
      },
    },
  });

  return (
    <ThemeProvider theme={articleStatsTheme}>
      <Box
        sx={{
          backgroundColor: `${pageColors.articleStats}15`,
          minHeight: '100vh',
          pt: { xs: 3, md: 4 },
          pb: { xs: 6, md: 8 },
          px: { xs: 1.5, sm: 3 },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              sx={{
                color: pageColors.articleStats,
                fontWeight: 'bold',
                mb: 3,
              }}
            >
              Article Sales
            </Typography>
            {loading && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <LinearProgress />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <DesktopDatePicker
                label="Start Date"
                inputFormat="YYYY-MM-DD"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => (
                  <params.TextField {...params} variant="outlined" size="small" />
                )}
              />
              <DesktopDatePicker
                label="End Date"
                inputFormat="YYYY-MM-DD"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => (
                  <params.TextField {...params} variant="outlined" size="small" />
                )}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                sx={{ alignSelf: 'center' }}
                disabled={loading}
              >
                Search
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'articleNumber'}
                        direction={orderBy === 'articleNumber' ? order : 'asc'}
                        onClick={() => handleRequestSort('articleNumber')}
                      >
                        <strong>Article #</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'articleDescription'}
                        direction={orderBy === 'articleDescription' ? order : 'asc'}
                        onClick={() => handleRequestSort('articleDescription')}
                      >
                        <strong>Description</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'totalQuantity'}
                        direction={orderBy === 'totalQuantity' ? order : 'asc'}
                        onClick={() => handleRequestSort('totalQuantity')}
                      >
                        <strong>Total Qty</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'totalSales'}
                        direction={orderBy === 'totalSales' ? order : 'asc'}
                        onClick={() => handleRequestSort('totalSales')}
                      >
                        <strong>Total Sales</strong>
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedStats.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.articleNumber}</TableCell>
                      <TableCell>{item.articleDescription}</TableCell>
                      <TableCell>{item.totalQuantity}</TableCell>
                      <TableCell>{item.totalSales}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Container>
        </LocalizationProvider>
      </Box>
    </ThemeProvider>
  );
}
